import React from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { cn } from "@/lib/utils";

const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      "target",
      "rel",
    ],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ["http", "https", "mailto"],
    src: ["http", "https"],
  },
};

type HastNode = {
  type: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const LINK_RE = /\bhttps?:\/\/[^\s<>"')\]]+|\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g;

function processChildren(children: HastNode[], parentTagName?: string): HastNode[] {
  const result: HastNode[] = [];
  for (const child of children) {
    if (child.type === "text" && parentTagName !== "a") {
      const text = child.value ?? "";
      const parts: HastNode[] = [];
      let lastIndex = 0;
      for (const match of text.matchAll(LINK_RE)) {
        const start = match.index!;
        const raw = match[0];
        const href = raw.startsWith("http") ? raw : `mailto:${raw}`;
        if (start > lastIndex) {
          parts.push({ type: "text", value: text.slice(lastIndex, start) });
        }
        parts.push({
          type: "element",
          tagName: "a",
          properties: { href },
          children: [{ type: "text", value: raw }],
        });
        lastIndex = start + raw.length;
      }
      if (parts.length === 0) {
        result.push(child);
      } else {
        if (lastIndex < text.length) {
          parts.push({ type: "text", value: text.slice(lastIndex) });
        }
        result.push(...parts);
      }
    } else if (child.children) {
      result.push({
        ...child,
        children: processChildren(child.children, child.tagName),
      });
    } else {
      result.push(child);
    }
  }
  return result;
}

function rehypeAutoLinkUrls() {
  return (tree: HastNode) => {
    if (tree.children) {
      tree.children = processChildren(tree.children);
    }
  };
}

interface MarkdownContentProps {
  children: string | null | undefined;
  className?: string;
}

export default function MarkdownContent({ children, className }: MarkdownContentProps) {
  if (!children) return null;
  return (
    <div
      className={cn(
        "prose prose-invert prose-sm max-w-none",
        "prose-p:text-zinc-300 prose-p:leading-relaxed prose-p:my-1",
        "prose-ul:text-zinc-300 prose-ul:my-1 prose-ul:pl-4",
        "prose-ol:text-zinc-300 prose-ol:my-1 prose-ol:pl-4",
        "prose-li:text-zinc-300 prose-li:my-0.5",
        "prose-strong:text-white prose-strong:font-semibold",
        "prose-a:text-primary prose-a:no-underline hover:prose-a:underline",
        "prose-headings:text-white prose-headings:font-semibold",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkBreaks, remarkGfm]}
        rehypePlugins={[rehypeAutoLinkUrls, [rehypeSanitize, sanitizeSchema]]}
        components={{
          a: ({ href, children: linkChildren, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {linkChildren}
            </a>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
